import React, { useEffect, useRef, useState, useCallback } from "react";
import { IconButton } from "@mui/material";
import SnFindFormContols from "./SnFindFormContols";
import { ValueChangedEvent } from "@shared/type/CommonType";
import SnLabel from "./SnLabel";

interface SnHeaderProps {
  headerInfo: { controls: any[]; data: any };
  onHeaderValueChanged?: (
    event: ValueChangedEvent
  ) => Record<string, any> | void;
  onButtonClick?: (sender: string) => void;
  onHeaderEnter?: (sender: string, value: string) => void;
}
type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

export function SnHeader({
  headerInfo,
  onHeaderValueChanged,
  onButtonClick,
  onHeaderEnter: onParentHeaderEnter,
}: SnHeaderProps) {
  const [haederControls, setFormControls] = useState<any>([]);
  const [headerData, setHeaderData] = useState<any>(null);
  const [visibleControls, setVisibleControls] = useState<any[]>([]);

  const [requireds, setRequireds] = useState<any>({});
  const [groups, setGroups] = useState<any[]>();
  const [filter, setFilter] = useState<any>();
  const [popupCons, setPopupCons] = useState<any>({}); /* */
  const refs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const inputEls = useRef<InputEls[]>([]);
  const headerDataRef = useRef(headerData);
  const [editCon, setEditCon] = useState<any>();
  const [focus, setFocus] = useState<boolean>();

  const setRef = (key: any, node: HTMLDivElement | null) => {
    if (node) {
      refs.current[key] = node; // key를 기준으로 DOM 요소를 저장
    }
  };

  const setInputRef = (
    el: HTMLElement | null,
    index: number,
    isReadOnly: boolean
  ) => {
    inputEls.current[index] = {
      el,
      isFocusable: !isReadOnly,
    };
  };
  const fetchControl = async () => {
    try {
      const visibleFilters = headerInfo?.controls.filter(
        (control) => control.fg_visible && control.fg_visible.trim() == "Y"
      );
      setVisibleControls(visibleFilters);
      setChangeCon(headerInfo?.data);
    } catch (error: any) {}
  };

  const fetchData = async () => {
    try {
      requestAnimationFrame(() => {
        setHeaderData(headerInfo?.data); // 상태 갱신 (폼 렌더링 유발)
        headerDataRef.current = headerInfo?.data;
        setFocus(true);
      });
    } catch (error: any) {}
  };

  useEffect(() => {
    fetchControl();
    fetchData();
  }, [headerInfo]);

  useEffect(() => {
    if (focus) {
      requestAnimationFrame(() => {
        focusInputByIndex(0);
        setFocus(false);
      });
    }
  }, [focus]);

  const focusInputByIndex = async (startIndex: number) => {
    let index = startIndex;
    while (index < inputEls.current.length) {
      const input = inputEls.current[index]?.el;
      const isFocusable = inputEls.current[index]?.isFocusable;
      if (input && isFocusable) {
        input.focus();
        break;
      }
      index++;
    }
  };

  const handleMoveFocus = (fromIndex: number, target: string) => {
    if (target === "current") {
      const el = inputEls.current?.[fromIndex].el;
      if (el) {
        el.focus();
      }
    } else if (target === "next") {
      const list = inputEls.current;
      for (let i = fromIndex + 1; i < list.length; i++) {
        const entry = list[i];
        if (entry?.el && entry.isFocusable) {
          entry.el.focus();
          return;
        }
      }
      // 끝까지 없으면 현재 blur
      list[fromIndex]?.el?.blur?.();
    }
  };

  const headerValueChangedEvent = async (event: ValueChangedEvent) => {
    if (onHeaderValueChanged) {
      return (await onHeaderValueChanged?.(event)) || {};
    }
  };

  const onTextValueChanged = useCallback(
    async (param: string, data: string) => {
      const prevValue = headerDataRef.current?.[param] ?? "";
      if (prevValue === data) return;

      const baseheaderData = { ...headerDataRef.current, [param]: data };
      formValueChanged(baseheaderData);
      const event: ValueChangedEvent = {
        oldValue: prevValue,
        newValue: data,
        colId: param,
        rowData: baseheaderData,
      };
      headerDataRef.current = baseheaderData;
      const updatedFields = await headerValueChangedEvent(event);
      const finalheaderData = { ...headerDataRef.current, ...updatedFields };

      formValueChanged(finalheaderData);
      setChangeCon(finalheaderData);
    },
    [headerData]
  );

  const onNumberValueChanged = useCallback(
    async (param: string, data: number) => {
      const prevValue = headerDataRef.current?.[param] ?? 0;
      if (prevValue === data) return;

      const baseheaderData = { ...headerData, [param]: data };
      formValueChanged(baseheaderData);
      const event: ValueChangedEvent = {
        oldValue: prevValue,
        newValue: data,
        colId: param,
        rowData: baseheaderData,
      };
      headerDataRef.current = baseheaderData;
      const updatedFields = await headerValueChangedEvent(event);
      const finalheaderData = { ...headerDataRef.current, ...updatedFields };
      formValueChanged(finalheaderData);
    },
    [headerData]
  );

  const onPeriodValueChanged = useCallback(
    async (param: string, start: string, end: string) => {
      const [startKey, endKey] = param.split(",").map((p) => p.trim());
      const prevStart = headerDataRef.current?.[startKey] ?? "";
      const prevEnd = headerDataRef.current?.[endKey] ?? "";

      if (prevStart === start && prevEnd === end) return;

      const baseheaderData = {
        ...headerDataRef.current,
        [startKey]: start,
        [endKey]: end,
      };
      formValueChanged(baseheaderData);
      let finalheaderData = baseheaderData;
      if (prevStart !== start) {
        const event: ValueChangedEvent = {
          oldValue: prevStart,
          newValue: start,
          colId: startKey,
          rowData: baseheaderData,
        };
        headerDataRef.current = baseheaderData;
        const updatedFields = await headerValueChangedEvent(event);
        finalheaderData = { ...headerDataRef.current, ...updatedFields };
      }
      if (prevEnd !== end) {
        const event: ValueChangedEvent = {
          oldValue: prevEnd,
          newValue: end,
          colId: endKey,
          rowData: baseheaderData,
        };
        headerDataRef.current = baseheaderData;
        const updatedFields = await headerValueChangedEvent(event);
        finalheaderData = { ...headerDataRef.current, ...updatedFields };
      }

      formValueChanged(finalheaderData);
      setChangeCon(finalheaderData);
    },
    [headerData]
  );

  const onCodeValueChanged = useCallback(
    async (
      code: string,
      codeValue: string,
      name: string,
      nameValue: string,
      rowData?: string
    ) => {
      const prevValue = headerDataRef.current?.[code] ?? "";
      if (prevValue === codeValue) return;

      let baseheaderData = {
        ...headerDataRef.current,
        [code]: codeValue,
        [name]: nameValue,
      };

      formValueChanged(baseheaderData);

      const event: ValueChangedEvent = {
        oldValue: prevValue,
        newValue: codeValue,
        colId: code,
        rowData: baseheaderData,
      };
      headerDataRef.current = baseheaderData;
      const updatedFields = await headerValueChangedEvent(event);
      const finalheaderData = { ...headerDataRef.current, ...updatedFields };

      formValueChanged(finalheaderData);
      setChangeCon(finalheaderData);
    },
    [headerData]
  );

  const formValueChanged = (data: any) => {
    headerDataRef.current = data;
    setHeaderData(data);
  };

  const onEnter = useCallback(
    (param: string, value: string) => {
      const currentData = value ?? "";

      if (onParentHeaderEnter) {
        onParentHeaderEnter(param, currentData);
      }
    },
    [onParentHeaderEnter]
  );

  const setChangeCon = useCallback(
    (change: any) => {
      const editObj: Record<string, any> = {};
      const filterObj: Record<string, any> = {};
      const visibleObj: Record<string, boolean> = {};
      const editCons = headerInfo?.controls.filter(
        (control) =>
          control.dc_editcon &&
          control.dc_editcon.length > 0 &&
          control.dc_editcon.trim() != ""
      );
      // editCon 처리
      if (editCons && change) {
        editCons.forEach((con) => {
          let conData = con.dc_editcon;

          if (change.state === "insert") {
            conData = conData.replace("new", "false");
          } else {
            conData = conData.replace("new", "true");
          }

          Object.keys(change).forEach((key) => {
            conData = conData.replaceAll(key, change[key]);
          });

          editObj[con.cd_param] = conData;
        });
        setEditCon(editObj);
      }
      const filters = headerInfo?.controls.filter(
        (control) =>
          control.dc_filter &&
          control.dc_filter.length > 0 &&
          control.dc_filter.trim() != ""
      );
      // filter 처리
      if (filters && change) {
        filters.forEach((fil) => {
          let filData = fil.dc_filter;
          Object.keys(change).forEach((key) => {
            filData = filData.replaceAll(`{${key}}`, change[key]);
          });
          filterObj[fil.cd_param] = filData;
        });
        setFilter(filterObj);
      }

      // visible 처리
      if (visibleControls) {
        visibleControls.forEach((control) => {
          const raw = editObj?.[control.cd_param];
          if (raw && change.hasOwnProperty(control.cd_param)) {
            try {
              visibleObj[control.cd_param] = Boolean(
                new Function(`return ${raw};`)()
              );
            } catch (e) {
              visibleObj[control.cd_param] = false;
              console.error("eval error in setChangeCon:", raw, e);
            }
          } else {
            visibleObj[control.cd_param] = false;
          }
        });
      }

      // 필요시 visibleObj 저장 또는 상태 처리 가능
    },
    [visibleControls, headerInfo?.controls]
  );

  const setReadOnly = useCallback(
    (param: string) => {
      const expr = editCon?.[param];
      if (!expr) return false;

      try {
        return Boolean(new Function(`return (${expr});`)());
      } catch (e) {
        console.error("eval error in setReadOnly:", expr, e);
        return false;
      }
    },
    [editCon]
  );

  return (
    <div className="flex justify-end items-center ml-2 gap-2">
      {visibleControls != null &&
        visibleControls.length > 0 &&
        visibleControls.map((control, index) => (
          <React.Fragment key={control.no_line ?? index}>
            {control.nm_label &&
              control.tp_ctrl.trim() !== "SnGroupLabel" &&
              control.tp_ctrl.trim() !== "SnButton" &&
              control.tp_ctrl.trim() !== "SnLinkedInfoBox" && (
                <SnLabel
                  text={control.nm_label}
                  type={"FreeForm"}
                  color={
                    Object.hasOwn(requireds, control.cd_param)
                      ? "#0b0c69"
                      : "black"
                  }
                />
              )}
            <div
              style={
                control.qt_width != null
                  ? { width: `${control.qt_width}px` }
                  : undefined
              }
            >
              <SnFindFormContols
                control={control}
                index={index}
                formData={headerData}
                requireds={requireds}
                popupCons={popupCons}
                setRef={setRef}
                setInputRef={setInputRef}
                handleMoveFocus={handleMoveFocus}
                onTextValueChanged={onTextValueChanged}
                onNumberValueChanged={onNumberValueChanged}
                onPeriodValueChanged={onPeriodValueChanged}
                onCodeValueChanged={onCodeValueChanged}
                setReadOnly={setReadOnly}
                onButtonClick={(param: string) => {
                  onButtonClick?.(param);
                }}
                onEnter={onEnter}
                filter={filter}
              />
            </div>
          </React.Fragment>
        ))}
    </div>
  );
}

export default React.memo(SnHeader);
